import { Button, Checkbox, Divider, Flex, Stack, Text } from "@mantine/core";
import { useCallback, useMemo } from "react";

import {
  baseLabelClassname,
  OTHER_FIELD_LABELS,
} from "../assessment-question-field";
import { QuestionWarningAlert } from "../../question-warning-alert";
import { PostPublishField } from "./post-publish-field";
import {
  AssessmentQuestionFields,
  type ReviewPageCompetency,
} from "@/types/screener-builder";
import { getPersistedWarning } from "@/utils/screener-builder";

interface PostPublishContentProps {
  competency: ReviewPageCompetency;
  isLegacyScreener: boolean;
  onClose: () => void;
}

const checkboxClassNames = { label: "text-uw-gray-9" };

export const PostPublishContent = ({
  competency: {
    custom,
    definition,
    displayName,
    hasCustomScoringGuidelines,
    name,
    questions,
    scoringGuidelines: { high, low, medium },
    warningBypassLog,
  },
  // Legacy screener competencies have a different data structure that we
  // massage to match the current structure. This creates competencies that will
  // never have a custom display name, will always have question text that
  // that matches the name, and have empty definitions. For this reason, we hide
  // related fields when rendering a legacy competency.
  isLegacyScreener,
  onClose,
}: PostPublishContentProps) => {
  const selectedQuestion = useMemo(
    () => questions.find(({ isSelected }) => isSelected)?.question ?? "",
    [questions],
  );
  const persistedWarning = useMemo(
    () => getPersistedWarning(warningBypassLog),
    [warningBypassLog],
  );
  const renderNonConfigField = useCallback(
    (label: string, content: string) => (
      <Flex direction="column">
        <Text className={baseLabelClassname} size="sm">
          {label}
        </Text>
        <Text size="sm">{content}</Text>
      </Flex>
    ),
    [],
  );
  const hasCustomDisplayName = displayName !== name;

  return (
    <>
      <Divider mb="24" />
      {persistedWarning && <QuestionWarningAlert warning={persistedWarning} />}
      <Stack gap={18}>
        <PostPublishField
          content={name}
          field={AssessmentQuestionFields.Competency}
        />

        {hasCustomDisplayName &&
          renderNonConfigField("Display name", displayName)}

        {!isLegacyScreener && (
          <Checkbox
            checked={hasCustomDisplayName}
            classNames={checkboxClassNames}
            disabled
            label={OTHER_FIELD_LABELS.customDisplayName}
          />
        )}

        {!isLegacyScreener && (
          <PostPublishField
            content={definition}
            field={AssessmentQuestionFields.Definition}
          />
        )}

        {!isLegacyScreener && (
          <PostPublishField
            content={selectedQuestion}
            field={AssessmentQuestionFields.Question}
          />
        )}

        {!isLegacyScreener && (
          <Checkbox
            checked={custom}
            classNames={checkboxClassNames}
            disabled
            label={OTHER_FIELD_LABELS.customQuestion}
          />
        )}

        {renderNonConfigField(OTHER_FIELD_LABELS.highScoringAnswer, high)}
        {renderNonConfigField(OTHER_FIELD_LABELS.mediumScoringAnswer, medium)}
        {renderNonConfigField(OTHER_FIELD_LABELS.lowScoringAnswer, low)}

        <Checkbox
          checked={hasCustomScoringGuidelines}
          classNames={checkboxClassNames}
          disabled
          label={OTHER_FIELD_LABELS.customRubric}
        />
      </Stack>

      <Divider my={24} />

      <Button onClick={onClose}>Close</Button>
    </>
  );
};
